<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :closable="false"
    :bodyStyle="{ padding: 0 }"
    width="680px"
    centered
    @cancel="handleClose"
    class="news-modal"
  >
    <div class="news-modal-content" v-if="news">
      <!-- Close Button -->
      <button class="news-modal-close" @click="handleClose">
        <a-icon type="close" /> Close
      </button>

      <!-- Cover Image -->
      <div class="news-modal-cover">
        <img v-if="news.image && news.image !== '_x_'" :src="news.image" :alt="news.title" />
        <div v-else class="news-modal-cover-empty"><a-empty :image="false" description="No Image" /></div>
      </div>

      <!-- Body -->
      <div class="news-modal-body">
        <!-- Title -->
        <h2 class="news-modal-title">{{ news.title }}</h2>

        <!-- Tags -->
        <div class="news-modal-tags">
          <a-tag
            v-for="(tag, index) in news.tags"
            :key="'tag-' + index + '-' + tag"
            :color="tagColor(tag)"
            class="news-tag"
          >
            {{ tag }}
          </a-tag>
        </div>

        <!-- Meta Info -->
        <div class="news-modal-meta">
          <div class="meta-item">
            <a-icon type="calendar" />
            <span>Published: <strong>{{ news.date }}</strong></span>
          </div>
          <div class="meta-item">
            <a-icon type="clock-circle" />
            <span>Expires: <strong>{{ news.expireDate }}</strong></span>
          </div>
        </div>

        <a-divider style="margin: 16px 0" />

        <!-- Description (HTML content) -->
        <div class="news-modal-description">
          <div class="desc-header">
            <a-icon type="read" />
            <span>Description</span>
          </div>
          <div class="desc-content" v-html="news.content"></div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
export default {
  name: "ModalNews",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    news: {
      type: Object,
      default: () => null,
    },
  },
  methods: {
    handleClose() {
      this.$emit("close");
    },
    tagColor(tag) {
      const colors = {
        Student: "blue",
        Students: "blue",
        Lecturer: "purple",
        Staff: "green",
        Faculty: "orange",
        "PC Lecturers": "cyan",
      };
      return colors[tag] || "default";
    },
  },
};
</script>

<style scoped>
.news-modal-content {
  position: relative;
}

/* Close Button */
.news-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;
  backdrop-filter: blur(4px);
}

.news-modal-close:hover {
  background: rgba(0, 0, 0, 0.85);
}

/* Cover Image */
.news-modal-cover {
  width: 100%;
  height: 260px;
  overflow: hidden;
}

.news-modal-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-modal-cover-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #bfbfbf;
}

/* Body */
.news-modal-body {
  padding: 24px 28px 28px;
}

/* Title */
.news-modal-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 12px;
  line-height: 1.4;
}

/* Tags */
.news-modal-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.news-tag {
  border-radius: 4px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Meta Info */
.news-modal-meta {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #595959;
}

.meta-item i {
  font-size: 16px !important;
  color: #8c8c8c;
}

.meta-item strong {
  color: #1a1a2e;
}

/* Description */
.news-modal-description {
  margin-top: 4px;
}

.desc-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 12px;
}

.desc-content {
  background: #f9f9fb;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 20px;
  font-size: 14px;
  line-height: 1.8;
  color: #595959;
  max-height: 300px;
  overflow-y: auto;
}

.desc-content >>> p {
  margin-bottom: 8px;
}

.desc-content >>> ul,
.desc-content >>> ol {
  padding-left: 20px;
  margin-bottom: 8px;
}

.desc-content >>> a {
  color: #502b80;
  text-decoration: underline;
}

.desc-content >>> strong {
  color: #1a1a2e;
}

/* Responsive */
@media (max-width: 768px) {
  .news-modal-cover {
    height: 180px;
  }
  .news-modal-body {
    padding: 20px;
  }
  .news-modal-title {
    font-size: 18px;
  }
}
</style>

<style>
/* Global modal overrides (unscoped) */
.news-modal .ant-modal-body {
  padding: 0 !important;
  border-radius: 16px;
  overflow: hidden;
}

.news-modal .ant-modal-content {
  border-radius: 16px !important;
  overflow: hidden;
}
</style>
